import { createRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { toast } from "@/lib/safe-toast";
import { Shield, AlertTriangle, Webhook, MessageSquare, DollarSign } from "lucide-react";
import { authenticatedRoute } from "../_authenticated";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  DataTable,
} from "@/components/ui";
import { useAuth } from "@/contexts/useAuth";
import {
  useProfile,
  useAbuseFlags,
  useWebhookFailures,
  useAdminFeedback,
  useUnpaidAffiliates,
  unpaidAffiliatesInvalidateKeys,
} from "@/hooks/useAdmin";
import type { AbuseFlag, WebhookFailure, FeedbackItem, UnpaidAffiliate } from "@/hooks/useAdmin";
import { apiPost } from "@/api";
import { toDisplayString, toSafeReactNode } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";

function AdminPage() {
  const { t, i18n } = useTranslation("common");
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const { data: profile, isLoading: profileLoading } = useProfile();
  const queryClient = useQueryClient();
  const locale = i18n.language || "en";

  const { data: flags, isLoading: flagsLoading } = useAbuseFlags();
  const { data: failures, isLoading: failuresLoading } = useWebhookFailures();
  const { data: feedback, isLoading: feedbackLoading } = useAdminFeedback();
  const { data: affiliates, isLoading: affiliatesLoading } = useUnpaidAffiliates();

  const [payingUserId, setPayingUserId] = useState<string | null>(null);

  const formatDate = (date: Date) => new Date(date).toLocaleDateString(toDisplayString(locale));

  if (profileLoading) {
    return <p className="text-sm text-text-muted">{toSafeReactNode(t("admin.loading"))}</p>;
  }

  if (!profile?.isAdmin) {
    return (
      <div className="space-y-6">
        <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("admin.title"))}</h1>
        <p className="text-sm text-text-muted">{toSafeReactNode(t("admin.adminOnly"))}</p>
      </div>
    );
  }

  const markPaid = async (affiliateUserId: string) => {
    if (!token) return;
    setPayingUserId(affiliateUserId);
    try {
      const res = await apiPost<{ markedPaid?: number }>("/admin/affiliate/payout", token, { affiliateUserId });
      if (res.ok) {
        toast.success(t("admin.markPaidSuccess", { count: res.data.markedPaid ?? 0 }));
        for (const queryKey of unpaidAffiliatesInvalidateKeys(token)) {
          void queryClient.invalidateQueries({ queryKey });
        }
      } else {
        toast.error(t("admin.payoutFailed"));
      }
    } catch {
      toast.error(t("admin.payoutError"));
    } finally {
      setPayingUserId(null);
    }
  };

  const flagColumns = [
    { id: "userId", header: toDisplayString(t("admin.user")), cell: (r: AbuseFlag) => `${toDisplayString(r.userId).slice(0, 8)}…` },
    { id: "reason", header: toDisplayString(t("admin.reason")), cell: (r: AbuseFlag) => toSafeReactNode(r.reason) },
    { id: "createdAt", header: toDisplayString(t("admin.date")), cell: (r: AbuseFlag) => formatDate(new Date(r.createdAt)) },
    { id: "resolved", header: toDisplayString(t("admin.status")), cell: (r: AbuseFlag) => toSafeReactNode(r.resolvedAt ? toDisplayString(t("admin.resolved")) : toDisplayString(t("admin.open"))) },
  ];

  const failureColumns = [
    { id: "eventType", header: toDisplayString(t("admin.eventType")), cell: (r: WebhookFailure) => toSafeReactNode(r.eventType) },
    { id: "error", header: toDisplayString(t("admin.error")), cell: (r: WebhookFailure) => toDisplayString(r.errorMessage).slice(0, 60) },
    { id: "createdAt", header: toDisplayString(t("admin.date")), cell: (r: WebhookFailure) => formatDate(new Date(r.createdAt)) },
  ];

  const feedbackColumns = [
    { id: "category", header: toDisplayString(t("admin.category")), cell: (r: FeedbackItem) => toSafeReactNode(r.category) },
    { id: "content", header: toDisplayString(t("admin.content")), cell: (r: FeedbackItem) => { const s = toDisplayString(r.content); return s.slice(0, 80) + (s.length > 80 ? "…" : ""); } },
    { id: "source", header: toDisplayString(t("admin.source")), cell: (r: FeedbackItem) => toSafeReactNode(r.source) },
    { id: "createdAt", header: toDisplayString(t("admin.date")), cell: (r: FeedbackItem) => formatDate(new Date(r.createdAt)) },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Shield className="h-5 w-5 text-accent" />
        <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("admin.title"))}</h1>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-accent" aria-hidden />
            <CardTitle>{toSafeReactNode(t("admin.affiliatePayoutsTitle"))}</CardTitle>
          </div>
          <CardDescription>{toSafeReactNode(t("admin.affiliatePayoutsDescription"))}</CardDescription>
        </CardHeader>
        <CardContent>
          {affiliatesLoading && <p className="text-sm text-text-muted">{toSafeReactNode(t("admin.loading"))}</p>}
          {affiliates && affiliates.length === 0 && (
            <p className="text-sm text-text-muted">{toSafeReactNode(t("admin.noPayouts"))}</p>
          )}
          {affiliates && affiliates.length > 0 && (
            <ul className="space-y-2">
              {affiliates.map((a: UnpaidAffiliate) => (
                <li
                  key={toDisplayString(a.userId)}
                  className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-2 px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <p className="truncate text-text">{toSafeReactNode(a.email ?? a.userId)}</p>
                    <p className="text-xs text-text-muted font-mono">{toDisplayString(a.userId).slice(0, 12)}…</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-text">
                      {(Number(a.unpaidCents) / 100).toFixed(2)} €
                    </span>
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={payingUserId === a.userId}
                      onClick={() => markPaid(a.userId)}
                    >
                      {payingUserId === a.userId ? "…" : toSafeReactNode(t("admin.markPaid"))}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden />
            <CardTitle>{toSafeReactNode(t("admin.abuseFlagsTitle"))}</CardTitle>
          </div>
          <CardDescription>{toSafeReactNode(t("admin.abuseFlagsDescription"))}</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable<AbuseFlag>
            columns={flagColumns}
            data={flags ?? []}
            keyFn={(r) => toDisplayString(r.id)}
            loading={flagsLoading}
            emptyMessage={toDisplayString(t("admin.noAbuseFlags"))}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Webhook className="h-5 w-5 text-danger" aria-hidden />
            <CardTitle>{toSafeReactNode(t("admin.webhookErrorsTitle"))}</CardTitle>
          </div>
          <CardDescription>{toSafeReactNode(t("admin.webhookErrorsDescription"))}</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable<WebhookFailure>
            columns={failureColumns}
            data={failures ?? []}
            keyFn={(r) => toDisplayString(r.id)}
            loading={failuresLoading}
            emptyMessage={toDisplayString(t("admin.noWebhookErrors"))}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-accent" aria-hidden />
            <CardTitle>{toSafeReactNode(t("admin.feedbackTitle"))}</CardTitle>
          </div>
          <CardDescription>{toSafeReactNode(t("admin.feedbackDescription"))}</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable<FeedbackItem>
            columns={feedbackColumns}
            data={feedback ?? []}
            keyFn={(r) => toDisplayString(r.id)}
            loading={feedbackLoading}
            emptyMessage={toDisplayString(t("admin.noFeedback"))}
          />
        </CardContent>
      </Card>
    </div>
  );
}

export const adminRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "admin",
  component: AdminPage,
});
